import Box from '@mui/material/Box';
import React, { useCallback, useEffect, useRef } from 'react';

import { ColumnResizeHandleProps } from './ColumnResizeHandle.props';

const DEFAULT_MIN_WIDTH = 20;
/** Pixels moved per Arrow key press for keyboard resizing. */
const KEYBOARD_STEP = 16;

const clampWidth = (value: number, minWidth: number, maxWidth?: number): number => {
  const lowerBounded = Math.max(minWidth, value);
  return maxWidth != null ? Math.min(maxWidth, lowerBounded) : lowerBounded;
};

const closestCell = (el: HTMLElement | null): HTMLElement | null =>
  (el?.closest('th, td') as HTMLElement | null) ?? null;

// offsetWidth is the layout (CSS) width — unaffected by the app's CSS `zoom`,
// and the value we persist as a column width.
const measureCellWidth = (el: HTMLElement | null): number =>
  closestCell(el)?.offsetWidth ?? DEFAULT_MIN_WIDTH;

/**
 * The app renders under CSS `zoom`, so `getBoundingClientRect()` and pointer
 * `clientX` are in visual (zoomed) pixels, while the width we write is unzoomed
 * CSS px. This ratio converts a visual delta back to CSS px (1 when unzoomed).
 */
const cssScale = (cell: HTMLElement): number => {
  const visual = cell.getBoundingClientRect().width;
  return cell.offsetWidth > 0 && visual > 0 ? visual / cell.offsetWidth : 1;
};

/**
 * Snapshot every data column's current CSS width (keyed by `data-col-key`) so
 * the table can freeze all not-yet-sized columns to their on-screen width at
 * the start of a resize — after which only the dragged column changes.
 */
const measureRowWidths = (el: HTMLElement | null): Record<string, number> => {
  const row = closestCell(el)?.closest('tr');
  const map: Record<string, number> = {};
  row?.querySelectorAll<HTMLElement>('th[data-col-key]').forEach((th) => {
    const key = th.dataset.colKey;
    if (key) map[key] = th.offsetWidth;
  });
  return map;
};

/**
 * The `<col>` in the table's `<colgroup>` that owns this column's width.
 *
 * Writing to it is what keeps a drag cheap: width is a property of the `<col>`,
 * so the browser re-lays-out the table without React re-rendering a single cell.
 *
 * Matched by cell index, which is exact rather than a guess: a `<colgroup>`
 * assigns its cols to columns positionally, so `cols[cellIndex]` is by definition
 * this cell's column. Every table here already relies on that alignment — the
 * checkbox, data and action cols are emitted under the same conditions as their
 * header cells — since otherwise widths would land on the wrong columns.
 *
 * Returns null for a table with no `<colgroup>`, where width is not a `<col>`
 * property; those fall back to committing through React on every pointer event.
 */
const findColElement = (el: HTMLElement | null): HTMLElement | null => {
  const cell = closestCell(el);
  const table = cell?.closest('table');
  if (!cell || !table) return null;
  const cols = table.querySelectorAll<HTMLElement>('colgroup > col');
  return cols[(cell as HTMLTableCellElement).cellIndex] ?? null;
};

/**
 * A thin, hover-revealed divider on the right edge of a table header cell.
 * Drag it (pointer) or Arrow-key it (keyboard) to resize the column.
 *
 * Dragging writes the width straight to the column's `<col>` element, once per
 * animation frame, and only commits through `onResize` on pointer-up. Committing
 * per pointer event instead meant a new `columns` array identity per event, which
 * defeated the row components' `React.memo` and re-rendered every cell in the
 * table — measured at ~106ms of main-thread blocking per event on a 25x10 grid,
 * i.e. a drag that should take 640ms taking 6.7s.
 *
 * Keyboard steps still commit immediately: they have no gesture to defer to.
 *
 * `onResize` also carries a one-time snapshot of every column's width, taken at
 * the start of the gesture, so the table can freeze all not-yet-sized columns
 * and only the dragged one changes. Stops propagation so it never starts the
 * header's drag-to-reorder or fires its sort click.
 */
export const ColumnResizeHandle: React.FC<ColumnResizeHandleProps> = ({
  columnKey,
  label,
  onResize,
  minWidth = DEFAULT_MIN_WIDTH,
  maxWidth,
}) => {
  // Teardown for an in-progress drag: removes the exact window listeners
  // registered at pointer-down and restores the body styles. Held in a ref so
  // the unmount effect can run it regardless of later re-renders.
  const teardownRef = useRef<(() => void) | null>(null);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      // Keep the header's dnd-kit drag-to-reorder and sort click from firing.
      e.stopPropagation();
      e.preventDefault();
      const cell = closestCell(e.currentTarget);
      if (!cell) return;
      const rect = cell.getBoundingClientRect();
      const colEl = findColElement(e.currentTarget);
      const drag = {
        cell,
        // Where the pointer grabbed relative to the cell's right edge (visual
        // px), so the grab point stays under the cursor throughout the drag.
        grabOffset: rect.right - e.clientX,
        scale: cssScale(cell),
        seed: measureRowWidths(e.currentTarget),
        // Measured once. Freezing siblings happens at pointer-down and nothing
        // else reflows the row during the gesture, so re-reading this per move
        // only bought a forced synchronous layout on every pointer event.
        left: rect.left,
      };

      let pendingWidth: number | null = null;
      let frame = 0;

      // Paint at most once per frame. Pointer events can outpace the display,
      // and each React commit here re-rendered every row in the table.
      const flush = () => {
        frame = 0;
        if (pendingWidth == null || !colEl) return;
        // Written on every frame the pointer moved, not just the first, so if an
        // unrelated re-render mid-drag (a list refetch, say) resets the width
        // from the now-stale React state, the next move corrects it.
        colEl.style.width = `${pendingWidth}px`;
      };

      const onMove = (ev: PointerEvent) => {
        const cssWidth = (ev.clientX + drag.grabOffset - drag.left) / drag.scale;
        const next = Math.round(clampWidth(cssWidth, minWidth, maxWidth));
        if (!colEl) {
          // No addressable <col>: keep the original live-commit behaviour so an
          // unmigrated table still resizes, just without the fast path.
          onResize(columnKey, next, drag.seed);
          return;
        }
        pendingWidth = next;
        if (!frame) frame = requestAnimationFrame(flush);
      };

      const onUp = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        if (frame) cancelAnimationFrame(frame);
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
        teardownRef.current = null;
        // Commit once, at the end. React state lags the DOM for the duration of
        // the gesture; this is what reconciles them (and drives persistence).
        if (colEl && pendingWidth != null) onResize(columnKey, pendingWidth, drag.seed);
      };

      teardownRef.current = onUp;
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'col-resize';
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    },
    [columnKey, onResize, minWidth, maxWidth]
  );

  // If the handle unmounts mid-drag, tear the drag down so the window listeners
  // and body styles don't leak.
  useEffect(() => () => teardownRef.current?.(), []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLElement>) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      e.preventDefault();
      e.stopPropagation();
      const current = measureCellWidth(e.currentTarget);
      const seed = measureRowWidths(e.currentTarget);
      const delta = e.key === 'ArrowRight' ? KEYBOARD_STEP : -KEYBOARD_STEP;
      onResize(columnKey, Math.round(clampWidth(current + delta, minWidth, maxWidth)), seed);
    },
    [columnKey, onResize, minWidth, maxWidth]
  );

  return (
    <Box
      component="button"
      type="button"
      className="col-resize-handle"
      onPointerDown={handlePointerDown}
      onKeyDown={handleKeyDown}
      onClick={(e) => e.stopPropagation()}
      aria-label={label ? `Resize ${label} column` : 'Resize column'}
      sx={{
        position: 'absolute',
        top: 0,
        right: 0,
        height: '100%',
        width: '10px',
        p: 0,
        m: 0,
        border: 'none',
        background: 'none',
        display: 'flex',
        justifyContent: 'center',
        cursor: 'col-resize',
        touchAction: 'none',
        opacity: 0,
        transition: 'opacity 0.15s ease',
        '&:focus-visible': { opacity: 1, outline: 'none' },
        // The visible divider: a subtle, non-full-height light-grey line.
        '&::after': {
          content: '""',
          width: '1px',
          my: '6px',
          bgcolor: 'v4.border',
          transition: 'background-color 0.15s ease',
        },
        '&:hover::after, &:focus-visible::after': {
          bgcolor: 'text.disabled',
        },
      }}
    />
  );
};
